import React from 'react';

import { type DailyRow } from '../../../redux/types/AnalystTypes';
import { fmt } from '../helpers';
import styles from '../Analyst.module.css';

const W = 1100;
const H = 220;
const PAD = { top: 14, right: 12, bottom: 26, left: 56 };
const PROFIT = '#1890ff';
const PAYOUT = '#22a06b';

// Прибыль по дням: столбец = прибыль с пополнений, сверху — с выплат; без внешних библиотек графиков
export const DailyChart = ({ rows }: { rows: DailyRow[] }) => {
  if (!rows.length) return <div className={styles.empty}>Нет данных за период</div>;
  const values = rows.map((r) => ({ payin: Number(r.profit_usdt), payout: Number(r.payout_profit_usdt) }));
  const max = Math.max(1e-9, ...values.map((v) => Math.max(0, v.payin) + Math.max(0, v.payout)));
  const min = Math.min(0, ...values.map((v) => Math.min(v.payin, 0)));
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  const y = (v: number) => PAD.top + ((max - v) / (max - min)) * innerH;
  const step = innerW / rows.length;
  const bar = Math.max(2, Math.min(28, step * 0.7));
  const labelEvery = Math.ceil(rows.length / 15);
  const tickDigits = max - min < 10 ? 2 : 0;

  return (
    <>
      <svg className={styles.chart} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio='none' role='img' aria-label='Прибыль по дням'>
        {[max, (max + min) / 2, min].map((tick) => (
          <g key={tick}>
            <line x1={PAD.left} x2={W - PAD.right} y1={y(tick)} y2={y(tick)} stroke='#eceef2' />
            <text x={PAD.left - 6} y={y(tick) + 4} fontSize='11' textAnchor='end' fill='#6b7280'>
              {fmt(tick, tickDigits)}
            </text>
          </g>
        ))}
        {rows.map((row, i) => {
          const v = values[i];
          const x = PAD.left + i * step + (step - bar) / 2;
          const payinTop = y(Math.max(v.payin, 0));
          const payinBottom = y(Math.min(v.payin, 0));
          return (
            <g key={row.date}>
              <title>
                {`${row.date}: прибыль ${fmt(row.total_profit_usdt)} USDT (пополнения ${fmt(row.profit_usdt)}, выплаты ${fmt(
                  row.payout_profit_usdt,
                )}), оплачено ${row.success_count} из ${row.created}`}
              </title>
              <rect x={x} y={payinTop} width={bar} height={Math.max(0, payinBottom - payinTop)} fill={v.payin < 0 ? '#fe5b37' : PROFIT} />
              {v.payout > 0 && (
                <rect x={x} y={y(Math.max(v.payin, 0) + v.payout)} width={bar} height={y(0) - y(v.payout)} fill={PAYOUT} />
              )}
              {i % labelEvery === 0 && (
                <text x={x + bar / 2} y={H - 8} fontSize='11' textAnchor='middle' fill='#6b7280'>
                  {row.date.slice(5).split('-').reverse().join('.')}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <div className={styles.chartLegend}>
        <span>
          <span className={styles.dot} style={{ background: PROFIT }} />
          Прибыль с пополнений, USDT
        </span>
        <span>
          <span className={styles.dot} style={{ background: PAYOUT }} />
          Прибыль с выплат, USDT
        </span>
      </div>
    </>
  );
};
